import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Layers,
  Lock,
  MoreHorizontal,
  Pencil,
  Search,
  Trash2,
  Upload,
} from "lucide-react";

import { DocumentFormDialog } from "@/components/DocumentFormDialog";
import { useConfirm } from "@/components/confirm-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useDeleteDocument } from "@/hooks/useDocumentMutations";
import { useDocuments } from "@/hooks/useDocuments";
import { FILE_TYPE_STYLES } from "@/lib/file-icons";
import { formatBytes, formatDate } from "@/lib/format";
import type { DocumentRow, FileType } from "@/lib/types";
import { cn } from "@/lib/utils";

const FILE_TYPES: { value: FileType | ""; label: string }[] = [
  { value: "",         label: "All types" },
  { value: "document", label: "Documents" },
  { value: "video",    label: "Videos" },
  { value: "audio",    label: "Audio" },
  { value: "image",    label: "Images" },
  { value: "other",    label: "Other" },
];

const PAGE_SIZE = 20;

/**
 * /private — the user's personal vault. Lists only documents whose
 * workflow_state is `owner_only` AND that the current user owns.
 * No state filter / no "scope" toggle — everything here is "Only
 * me" by definition. Upload from this page defaults to "Only me"
 * visibility (kept in sync via the dialog's defaultVisibility prop).
 */
export default function PrivateLibrary() {
  const [search,      setSearch]      = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [fileType,    setFileType]    = useState<FileType | "">("");
  const [page,        setPage]        = useState(1);
  const [uploadOpen,  setUploadOpen]  = useState(false);
  const [editing,     setEditing]     = useState<DocumentRow | null>(null);

  const { data, isLoading, isFetching } = useDocuments({
    search:   search || undefined,
    fileType: fileType || undefined,
    audience: "private",
    page,
  });

  function onSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSearch(searchDraft.trim());
    setPage(1);
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.count / PAGE_SIZE)) : 1;

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 text-slate-50 shadow-soft">
            <Lock className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Private vault</h1>
            <p className="text-sm text-muted-foreground">
              Files only you can see. To share with the staff portal or the public site,
              open the <Link to="/library" className="text-primary hover:underline">Shared library</Link> instead.
            </p>
          </div>
        </div>
        <Button onClick={() => setUploadOpen(true)}>
          <Upload className="mr-2 h-4 w-4" />
          Upload to vault
        </Button>
      </header>

      {/* Upload defaults to "Only me" so a file added from this page
          stays here unless the user explicitly opts to share. */}
      <DocumentFormDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        defaultVisibility="private"
      />
      <DocumentFormDialog
        open={editing !== null}
        onOpenChange={(o) => !o && setEditing(null)}
        document={editing}
      />

      {/* ---------- Filter bar (lean: no state filter, no scope) ---------- */}
      <Card className="border-border/60 shadow-soft">
        <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center">
          <form onSubmit={onSearchSubmit} className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search your private files…"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              className="h-10 pl-9 pr-3"
            />
          </form>
          <select
            value={fileType}
            onChange={(e) => { setFileType(e.target.value as FileType | ""); setPage(1); }}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm font-medium"
          >
            {FILE_TYPES.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </CardContent>
      </Card>

      {/* ---------- Table ---------- */}
      <Card className="overflow-hidden border-border/60 shadow-soft">
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-muted/30">
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-5">File</TableHead>
                <TableHead className="hidden lg:table-cell">Size</TableHead>
                <TableHead className="hidden xl:table-cell">Updated</TableHead>
                <TableHead className="w-16 text-right pr-5" />
              </TableRow>
            </TableHeader>

            <TableBody>
              {isLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={4} className="py-4">
                      <Skeleton className="h-10 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : data && data.results.length > 0 ? (
                data.results.map((doc) => {
                  const style = FILE_TYPE_STYLES[doc.file_type];
                  const Icon  = style.icon;
                  return (
                    <TableRow key={doc.id} className="border-border/60">
                      <TableCell className="py-3 pl-5">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={cn(
                            "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
                            style.chipClass,
                          )}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <Link
                                to={`/library/${doc.id}`}
                                className="truncate text-sm font-medium hover:text-primary hover:underline"
                              >
                                {doc.title}
                              </Link>
                              {/* Multi-file pip: only when this resource
                                  bundles >1 attachment. Single-file docs
                                  stay clean. */}
                              {doc.file_count > 1 && (
                                <span
                                  className="inline-flex shrink-0 items-center gap-0.5 rounded-full border border-primary/30 bg-primary/10 px-1.5 py-px text-[10px] font-semibold text-primary"
                                  title={`Bundle of ${doc.file_count} files`}
                                >
                                  <Layers className="h-3 w-3" />
                                  {doc.file_count}
                                </span>
                              )}
                            </div>
                            <div className="text-xs font-mono uppercase text-muted-foreground">
                              {doc.file_extension || doc.file_type}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">
                        {doc.file_size_bytes ? formatBytes(doc.file_size_bytes) : "—"}
                      </TableCell>
                      <TableCell className="hidden xl:table-cell text-xs text-muted-foreground">
                        {formatDate(doc.updated_at)}
                      </TableCell>
                      <TableCell className="pr-5 text-right">
                        <RowActions
                          doc={doc}
                          onEdit={() => setEditing(doc)}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="py-16 text-center">
                    <EmptyState onUpload={() => setUploadOpen(true)} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {data && data.count > PAGE_SIZE && (
        <div className="flex items-center justify-between gap-4">
          <div className="text-sm text-muted-foreground">
            <span className="font-mono font-semibold tabular-nums">{data.count.toLocaleString()}</span>{" "}
            private file{data.count === 1 ? "" : "s"}
            {isFetching && <span className="ml-2 text-xs">loading…</span>}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={!data.previous}
              onClick={() => setPage((p) => Math.max(1, p - 1))}>
              <ChevronLeft className="h-4 w-4" />Previous
            </Button>
            <div className="rounded-md border bg-card px-3 py-1.5 text-sm font-mono tabular-nums text-muted-foreground">
              {page} / {totalPages}
            </div>
            <Button variant="outline" size="sm" disabled={!data.next}
              onClick={() => setPage((p) => p + 1)}>
              Next<ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function RowActions({ doc, onEdit }: { doc: DocumentRow; onEdit: () => void }) {
  const del = useDeleteDocument();
  const confirm = useConfirm();

  async function onDelete() {
    const ok = await confirm({
      title: `Delete "${doc.title}"?`,
      description: "This cannot be undone.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    del.mutate(doc.id);
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        {doc.file_url && (
          <DropdownMenuItem asChild>
            <a href={doc.file_url} target="_blank" rel="noreferrer">
              <ExternalLink className="mr-2 h-4 w-4" />Open file
            </a>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={onEdit}>
          <Pencil className="mr-2 h-4 w-4" />Edit
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          disabled={del.isPending}
          onSelect={(e) => { e.preventDefault(); onDelete(); }}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          {del.isPending ? "Deleting…" : "Delete"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function EmptyState({ onUpload }: { onUpload: () => void }) {
  return (
    <div className="mx-auto max-w-md space-y-3">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-muted">
        <Lock className="h-5 w-5 text-muted-foreground" />
      </div>
      <h3 className="text-sm font-semibold">Your vault is empty</h3>
      <p className="text-xs text-muted-foreground">
        Anything you upload here stays private to your account — nobody else, not even
        directors, sees it unless you re-share it via the library.
      </p>
      <Button size="sm" onClick={onUpload}>
        <Upload className="mr-2 h-4 w-4" />Add your first file
      </Button>
    </div>
  );
}

